import { useEffect, useMemo, useState } from "react"
import { Button, makeStyles, Typography } from "@material-ui/core"
import {
  ClientFragment,
  Enum_Safety_Plan_Status_Enum,
  InsertSafetyPlanDocument,
  InsertSafetyPlanMutation,
  InsertSafetyPlanMutationVariables,
  Enum_Safety_Plan_Type_Enum,
  GetPlanByUserIdAndTemplateNameQuery,
} from "../../../generated/graphql"
import { useGraphQLClient } from "../../../hooks/useGraphQLClient"
import { useSafetyPlanSections } from "../../../hooks/useSafetyPlanSections"
import { useUser } from "../../../hooks/useUser"
import {
  ConfirmSafetyPlan,
  planTypeToString,
  SafetyPlanSectionsPractitionerProps,
  Section,
  Selected,
  UpdateSafetyPlanOption,
  updateSelectedItems,
} from "../../../utils/safetyPlanUtils"
import { colors } from "../../../utils/theme"

import ConfirmSaveButton from "../../molecules/safetyPlan/ConfirmSaveButton"
import EditableSafetyPlanSection from "../../molecules/safetyPlan/EditableSafetyPlanSection"
import SafetyPlanCheckList from "../../molecules/safetyPlan/SafetyPlanChecklist"
import SafetyPlanPdfDownloadButton from "../../molecules/safetyPlan/SafetyPlanPdfDownloadButton"
import SafetyPlanFinishedDialog from "./SafetyPlanFinishedDialog"
import useSafetyPlanStyles from "./safetyPlan.styles"

function SafetyPlanSections({
  options,
  selection,
  onChange,
  onClick,
  openSection,
  collapsed,
}: SafetyPlanSectionsPractitionerProps) {
  const sectionsArr = options.size > 0 ? [...options.values()] : []

  const sections = sectionsArr.map((d: Section, index: number) => (
    <EditableSafetyPlanSection
      selected={d}
      options={options}
      selection={selection}
      onChange={onChange}
      onClick={onClick}
      openSection={openSection}
      collapsed={collapsed}
      key={index.toString()}
    />
  ))
  return <>{sections}</>
}

/**
 * Allows a practitioner to create a new safety plan for a given client from scratch.
 * Renders editable plan sections, tracks selections, and persists the plan via GraphQL
 * once the practitioner confirms. The client can subsequently view and edit the plan
 * in the Vira mobile app.
 *
 * @param client - The client for whom the safety plan is being created.
 */
export default function SafetyPlanPractitioner({
  client,
  planData,
}: {
  client: ClientFragment
  planData: GetPlanByUserIdAndTemplateNameQuery | undefined
}) {
  const { user } = useUser()
  const graphqlClient = useGraphQLClient()

  const template = planData?.safety_plan_template[0]
  const templateId = template?.id
  const planType = template?.plan_type
  const sectionData = template?.sections

  const emptyOptionIds = useMemo(() => [], [])
  const emptyOptionsSet = useMemo(() => new Set<string>(), [])
  const { responseOptions, selectedOptions } = useSafetyPlanSections({
    sectionData,
    optionIds: emptyOptionIds,
    optionsSet: emptyOptionsSet,
  })

  const clientName = `${client.first_name} ${client.last_name}`

  const [isVisible, setIsVisible] = useState(false)
  const [showSave, setShowSave] = useState(false)
  const [openPlanDone, setOpenPlanDone] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Selected[]>([])
  const [collapsed, setCollapsed] = useState<string[]>([])
  const [updatedSafetyPlan, setUpdatedSafetyPlan] = useState<
    UpdateSafetyPlanOption[]
  >([])

  const showChecklist = useMemo(
    () => selectedItems.some((el) => el.category_type === "radio"),
    [selectedItems]
  )

  const clickHandler = (id: string, value: any, type: string) => {
    setShowSave(true)
    setSelectedItems((prev) => updateSelectedItems(prev, id, value, type))
  }

  const customHandler = (id: string, value: string[], type: string) => {
    const filtered = selectedItems.filter((el) => el.id !== id)
    setSelectedItems([...filtered, { id, value, category_type: type }])
    setShowSave(true)
  }

  const openSection = (id: any) => {
    const found = collapsed.includes(id)
    if (found) {
      setCollapsed((prev) => prev.filter((el) => el !== id))
    } else {
      setCollapsed((prev) => [...prev, id])
    }
  }

  const submitHandler = () => {
    setUpdatedSafetyPlan(selectedItems)
    setIsVisible(true)
  }

  const confirmSafetyPlan = async () => {
    const selected: ConfirmSafetyPlan[] = updatedSafetyPlan.map((update) => ({
      id: update.id,
      custom_values: update.value,
    }))
    await graphqlClient.request<
      InsertSafetyPlanMutation,
      InsertSafetyPlanMutationVariables
    >(InsertSafetyPlanDocument, {
      status: Enum_Safety_Plan_Status_Enum.Accepted,
      userId: client.id,
      updatedBy: user.id, // practitioner id
      safetyPlanTemplateId: templateId,
      responses: { selected },
    })
    setIsVisible(!isVisible)
    setOpenPlanDone(true)
  }

  useEffect(() => {
    setCollapsed([...responseOptions.keys()])
  }, [responseOptions])

  const localStyles = useStyles()
  const sharedStyles = useSafetyPlanStyles()
  const classes = { ...localStyles, ...sharedStyles }

  if (!templateId) return null

  return (
    <>
      <div id="plan" className={classes.planContainer}>
        <div id="pl" className={classes.planContent}>
          <Typography id="plnTxt" className={classes.planText}>
            Please create your client's {planTypeToString(planType, false)}.
            Your client will be able to view and edit this plan in the Vira app.
          </Typography>
        </div>

        <div id="planDetails" className={classes.planDetails}>
          <div className={classes.planDetailsContainer}>
            <Typography
              variant="h2"
              style={{ color: colors.safetyPlan.ink, lineHeight: "40px" }}
            >
              {clientName}'s {planTypeToString(planType, true)}
            </Typography>
            {planType === Enum_Safety_Plan_Type_Enum.Safety && (
              <SafetyPlanPdfDownloadButton
                clientName={clientName}
                selectedOptions={selectedOptions}
              />
            )}
          </div>

          <SafetyPlanSections
            options={responseOptions}
            onClick={clickHandler}
            selection={selectedItems}
            onChange={customHandler}
            openSection={openSection}
            collapsed={collapsed}
            onSave={submitHandler}
            showSave={showSave}
          />
          {showChecklist && (
            <SafetyPlanCheckList name={client.first_name ?? ""} />
          )}
          {showSave && (
            <div>
              <Button className={classes.saveModalBtn} onClick={submitHandler}>
                <Typography
                  className={classes.btnText}
                  style={{ color: colors.neutral.white }}
                >
                  Save
                </Typography>
              </Button>
            </div>
          )}
        </div>
        {isVisible && <ConfirmSaveButton onSave={confirmSafetyPlan} />}
        <SafetyPlanFinishedDialog
          planType={planType}
          open={openPlanDone}
          onClose={() => setOpenPlanDone(false)}
        />
      </div>
    </>
  )
}

const useStyles = makeStyles(() => ({
  saveModalBtn: {
    display: "flex",
    height: "52px",
    padding: "4px 32px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "200px",
    background: colors.primary.shadow,
    textTransform: "none",
    width: "100%",
    marginLeft: "0px !important",
    "&:hover": {
      background: colors.primary.shadow,
    },
  },
  btnText: {
    color: colors.primary.shadow,
    textAlign: "center",
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: "24px",
  },
}))
